import type { FlowDefinition, FlowNode, FlowPortKind, FlowValueType } from '../types';
import { VASE_FLOW_FIELD_IDS } from './flows';
import { REQUEST_FLOW_FIELD_IDS } from './flowFieldExtensions';

export type FlowNodeLibraryCategory =
  | 'context'
  | 'actions'
  | 'data'
  | 'display'
  | 'all';

export type FlowNodeTier = 'hero' | 'utility';
export type FlowNodeKind = 'block' | 'logic';
export type CompareOperator =
  | 'equals'
  | 'notEquals'
  | 'greaterThan'
  | 'lessThan'
  | 'contains'
  | 'isEmpty'
  | 'isNotEmpty';

export type ConfigurableValueType =
  | 'context.selection'
  | 'context.item'
  | 'entity.product'
  | 'entity.request-item'
  | 'scalar.string'
  | 'scalar.number'
  | 'scalar.boolean'
  | 'list.string'
  | 'list.request-item'
  | 'entity.combination';

export interface FlowNodeSocketDefinition {
  id: string;
  label: string;
  direction: 'input' | 'output';
  type?: FlowValueType;
  portKind?: FlowPortKind;
  conceptType?: 'selection' | 'item';
  valueOrigin?: 'produced' | 'through';
  required?: boolean;
  defaultVisible?: boolean;
  resolveType?: (node: FlowNode, flow: FlowDefinition | null) => FlowValueType;
}

export interface FlowNodeDefinition {
  type: string;
  label: string;
  description: string;
  tier: FlowNodeTier;
  kind: FlowNodeKind;
  category: Exclude<FlowNodeLibraryCategory, 'all'>;
  sockets: FlowNodeSocketDefinition[];
  defaultConfig?: Record<string, unknown>;
}

export function getSelectionCollectorInputSocketId(index: number) {
  return `selection${index}`;
}

export function isSelectionCollectorInputSocketId(socketId: string) {
  return /^selection\d+$/.test(socketId);
}

export function getTriggerCollectorInputSocketId(index: number) {
  return `event${index}`;
}

export function isTriggerCollectorInputSocketId(socketId: string) {
  return /^event\d+$/.test(socketId);
}

export function getSelectionCollectorInputCount(node: FlowNode | null | undefined) {
  const configuredCount =
    node && Number.isFinite(node.config?.inputCount)
      ? Math.max(2, Math.round(Number(node.config?.inputCount)))
      : 2;
  return configuredCount;
}

export function getTriggerCollectorInputCount(node: FlowNode | null | undefined) {
  const configuredCount =
    node && Number.isFinite(node.config?.inputCount)
      ? Math.max(2, Math.round(Number(node.config?.inputCount)))
      : 2;
  return configuredCount;
}

function getConfiguredSocketOrder(
  node: FlowNode | null | undefined,
  direction: 'input' | 'output'
) {
  const value =
    direction === 'input' ? node?.config?.socketOrderInput : node?.config?.socketOrderOutput;
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === 'string' && entry.length > 0)
    : [];
}

export type SelectionCollectorMode = 'latest' | 'accumulate' | 'append';

export function getSelectionCollectorMode(node: FlowNode | null | undefined): SelectionCollectorMode {
  const configuredMode = node?.config?.collectorMode;
  return configuredMode === 'accumulate' || configuredMode === 'append' ? configuredMode : 'latest';
}

export function getAddToListItemSocketId(index: number) {
  return index <= 1 ? 'item' : `item${index}`;
}

export function isAddToListItemSocketId(socketId: string) {
  return socketId === 'item' || /^item\d+$/.test(socketId);
}

export function getAddToListItemCount(node: FlowNode | null | undefined) {
  const configuredCount =
    node && Number.isFinite(node.config?.itemCount)
      ? Math.max(1, Math.round(Number(node.config?.itemCount)))
      : 1;
  return configuredCount;
}

function buildSelectionCollectorSockets(node: FlowNode | null | undefined): FlowNodeSocketDefinition[] {
  const inputCount = getSelectionCollectorInputCount(node);
  const inputSockets = Array.from({ length: inputCount }, (_, index) => ({
    id: getSelectionCollectorInputSocketId(index + 1),
    label: `Choice ${index + 1}`,
    direction: 'input' as const,
    type: 'context.selection' as const,
    portKind: 'context' as const,
    conceptType: 'selection' as const,
    defaultVisible: true,
  }));
  return [
    ...inputSockets,
      {
        id: 'removeItemId',
        label: 'Removed Item ID',
        direction: 'input',
        type: 'scalar.string',
        portKind: 'data',
        defaultVisible: true,
      },
    {
      id: 'selection',
      label: 'Current Choice',
      direction: 'output',
      type: 'context.selection',
      portKind: 'context',
      conceptType: 'selection',
      defaultVisible: true,
    },
    {
      id: 'product',
      label: 'Product',
      direction: 'output',
      type: 'entity.product',
      portKind: 'context',
      conceptType: 'selection',
      defaultVisible: true,
    },
    {
      id: 'items',
      label: 'Request Items',
      direction: 'output',
      type: 'list.request-item',
      portKind: 'data',
      defaultVisible: true,
    },
  ];
}

function buildTriggerCollectorSockets(node: FlowNode | null | undefined): FlowNodeSocketDefinition[] {
  const inputCount = getTriggerCollectorInputCount(node);
  const inputSockets = Array.from({ length: inputCount }, (_, index) => ({
    id: getTriggerCollectorInputSocketId(index + 1),
    label: `Event ${index + 1}`,
    direction: 'input' as const,
    type: 'event.trigger' as const,
    portKind: 'trigger' as const,
    defaultVisible: true,
  }));
  return [
    ...inputSockets,
    {
      id: 'trigger',
      label: 'Run',
      direction: 'output',
      type: 'event.trigger',
      portKind: 'trigger',
      defaultVisible: true,
    },
  ];
}

function buildAddToListSockets(node: FlowNode | null | undefined): FlowNodeSocketDefinition[] {
  const itemCount = getAddToListItemCount(node);
  const itemSockets = Array.from({ length: itemCount }, (_, index) => ({
    id: getAddToListItemSocketId(index + 1),
    label: itemCount === 1 && index === 0 ? 'Request Item' : `Request Item ${index + 1}`,
    direction: 'input' as const,
    type: 'context.item' as const,
    portKind: 'context' as const,
    conceptType: 'item' as const,
    required: index === 0,
    defaultVisible: true,
  }));

  return [
    {
      id: 'trigger',
      label: 'Run',
      direction: 'input',
      type: 'event.trigger',
      portKind: 'trigger',
      defaultVisible: true,
    },
    {
      id: 'removeItemId',
      label: 'Removed Item ID',
      direction: 'input',
      type: 'scalar.string',
      portKind: 'data',
      defaultVisible: true,
    },
    ...itemSockets,
    { id: 'list', label: 'Request Items', direction: 'input', type: 'list.request-item', required: true, portKind: 'data' },
    {
      id: 'updatedList',
      label: 'Updated Request',
      direction: 'output',
      type: 'list.request-item',
      portKind: 'data',
      defaultVisible: true,
    },
  ];
}

function applyConfiguredSocketOrder(
  node: FlowNode | null | undefined,
  sockets: FlowNodeSocketDefinition[]
) {
  const orderDirection = (direction: 'input' | 'output') => {
    const order = getConfiguredSocketOrder(node, direction);
    const indexById = new Map(order.map((id, index) => [id, index]));
    return sockets
      .filter((socket) => socket.direction === direction)
      .sort((left, right) => {
        const leftIndex = indexById.get(left.id);
        const rightIndex = indexById.get(right.id);
        if (leftIndex != null && rightIndex != null) return leftIndex - rightIndex;
        if (leftIndex != null) return -1;
        if (rightIndex != null) return 1;
        return sockets.indexOf(left) - sockets.indexOf(right);
      });
  };

  return [...orderDirection('input'), ...orderDirection('output')];
}

export type FlowNodeLibraryItem =
  | {
      id: string;
      kind: 'logic-node';
      nodeType: string;
      label: string;
      description: string;
      category: Exclude<FlowNodeLibraryCategory, 'all'>;
    }
  | {
      id: string;
      kind: 'preset';
      presetId: string;
      label: string;
      description: string;
      category: Exclude<FlowNodeLibraryCategory, 'all'>;
    };

export const FLOW_NODE_LIBRARY_CATEGORY_LABELS: Record<FlowNodeLibraryCategory, string> = {
  all: 'All',
  context: 'Shared State',
  actions: 'Actions',
  data: 'Checks & Values',
  display: 'Page Blocks',
};

function getConfiguredValueType(node: FlowNode, fallback: ConfigurableValueType): FlowValueType {
  const configuredType = node.config?.valueType;
  if (typeof configuredType !== 'string') return fallback;
  const allowed: ConfigurableValueType[] = [
    'context.selection',
    'context.item',
    'entity.product',
    'entity.request-item',
    'scalar.string',
    'scalar.number',
    'scalar.boolean',
    'list.string',
    'list.request-item',
    'entity.combination',
  ];
  return allowed.includes(configuredType as ConfigurableValueType)
    ? (configuredType as FlowValueType)
    : fallback;
}

function getSourceSelectorOutputType(node: FlowNode, flow: FlowDefinition | null): FlowValueType {
  const selectedFieldId = typeof node.config?.fieldId === 'string' ? node.config.fieldId : '';
  const fieldType = flow?.fields.find((field) => field.id === selectedFieldId)?.type;
  return fieldType ?? 'scalar.string';
}

export const FLOW_NODE_DEFINITIONS: Record<string, FlowNodeDefinition> = {
  button: {
    type: 'button',
    label: 'Button',
    description: 'Starts an action when the visitor clicks it.',
    tier: 'utility',
    kind: 'block',
    category: 'display',
    sockets: [
      {
        id: 'click',
        label: 'Clicked',
        direction: 'output',
        type: 'event.trigger',
        portKind: 'trigger',
        defaultVisible: true,
      },
    ],
  },
  gallery: {
    type: 'gallery',
    label: 'Product Gallery',
    description: 'Lets the visitor choose a product from a gallery to drive the rest of the journey.',
    tier: 'utility',
    kind: 'block',
    category: 'display',
    sockets: [
      {
        id: 'product',
        label: 'Current Choice',
        direction: 'output',
        type: 'context.selection',
        portKind: 'context',
        conceptType: 'selection',
        defaultVisible: true,
      },
      {
        id: 'select',
        label: 'Selected',
        direction: 'output',
        type: 'event.trigger',
        portKind: 'trigger',
      },
    ],
  },
  'product-card': {
    type: 'product-card',
    label: 'Product Card',
    description: 'Lets the visitor choose a product and start the rest of the journey.',
    tier: 'utility',
    kind: 'block',
    category: 'display',
    sockets: [
      {
        id: 'product',
        label: 'Current Choice',
        direction: 'output',
        type: 'context.selection',
        portKind: 'context',
        conceptType: 'selection',
        defaultVisible: true,
      },
      {
        id: 'select',
        label: 'Selected',
        direction: 'output',
        type: 'event.trigger',
        portKind: 'trigger',
      },
    ],
  },
  'customiser-viewer': {
    type: 'customiser-viewer',
    label: '3D Viewer',
    description: 'Shows the current product choice in 3D and reacts to open, close, and reset actions.',
    tier: 'hero',
    kind: 'block',
    category: 'display',
    sockets: [
      {
        id: 'selection',
        label: 'Current Choice',
        direction: 'input',
        type: 'context.selection',
        portKind: 'context',
        conceptType: 'selection',
        defaultVisible: true,
      },
      {
        id: 'product',
        label: 'Product',
        direction: 'input',
        type: 'entity.product',
        portKind: 'context',
        conceptType: 'selection',
        defaultVisible: true,
      },
      {
        id: 'finish',
        label: 'Finish',
        direction: 'input',
        type: 'scalar.string',
        portKind: 'data',
        defaultVisible: true,
      },
      {
        id: 'combination',
        label: 'Colourway',
        direction: 'input',
        type: 'entity.combination',
        portKind: 'data',
        defaultVisible: true,
      },
      {
        id: 'viewerMode',
        label: 'Mode',
        direction: 'input',
        type: 'scalar.string',
        portKind: 'data',
        defaultVisible: true,
      },
      {
        id: 'openViewer',
        label: 'Open',
        direction: 'input',
        type: 'event.trigger',
        portKind: 'trigger',
        defaultVisible: true,
      },
      {
        id: 'closeViewer',
        label: 'Close',
        direction: 'input',
        type: 'event.trigger',
        portKind: 'trigger',
        defaultVisible: true,
      },
      {
        id: 'cameraPreset',
        label: 'Camera',
        direction: 'input',
        type: 'scalar.string',
        portKind: 'data',
      },
      {
        id: 'resetView',
        label: 'Reset View',
        direction: 'input',
        type: 'event.trigger',
        portKind: 'trigger',
        defaultVisible: true,
      },
      {
        id: 'onViewerOpened',
        label: 'Opened',
        direction: 'output',
        type: 'event.trigger',
        portKind: 'trigger',
        defaultVisible: true,
      },
      {
        id: 'onViewerClosed',
        label: 'Closed',
        direction: 'output',
        type: 'event.trigger',
        portKind: 'trigger',
        defaultVisible: true,
      },
      {
        id: 'onModelLoaded',
        label: 'Model Loaded',
        direction: 'output',
        type: 'event.trigger',
        portKind: 'trigger',
        defaultVisible: true,
      },
      {
        id: 'activeProduct',
        label: 'Product',
        direction: 'output',
        type: 'entity.product',
        portKind: 'context',
        conceptType: 'selection',
        defaultVisible: true,
      },
      {
        id: 'activeFinish',
        label: 'Finish',
        direction: 'output',
        type: 'scalar.string',
        portKind: 'data',
        defaultVisible: true,
      },
      {
        id: 'activeCombination',
        label: 'Colourway',
        direction: 'output',
        type: 'entity.combination',
        portKind: 'data',
        defaultVisible: true,
      },
    ],
  },
  'customiser-finish-picker': {
    type: 'customiser-finish-picker',
    label: 'Finish Picker',
    description: 'Updates the active selection with a finish choice.',
    tier: 'utility',
    kind: 'block',
    category: 'display',
    sockets: [
      {
        id: 'selection',
        label: 'Current Choice',
        direction: 'input',
        type: 'context.selection',
        portKind: 'context',
        conceptType: 'selection',
        defaultVisible: true,
      },
      {
        id: 'reset',
        label: 'Reset',
        direction: 'input',
        type: 'event.trigger',
        portKind: 'trigger',
        defaultVisible: true,
      },
      {
        id: 'product',
        label: 'Product',
        direction: 'input',
        type: 'entity.product',
        portKind: 'context',
        conceptType: 'selection',
        defaultVisible: true,
      },
      {
        id: 'finish',
        label: 'Chosen Finish',
        direction: 'output',
        type: 'scalar.string',
        portKind: 'data',
        defaultVisible: true,
      },
      {
        id: 'finishSelected',
        label: 'Finish Picked',
        direction: 'output',
        type: 'event.trigger',
        portKind: 'trigger',
        defaultVisible: true,
      },
      {
        id: 'updatedSelection',
        label: 'Updated Choice',
        direction: 'output',
        type: 'context.selection',
        portKind: 'context',
        conceptType: 'selection',
        defaultVisible: true,
      },
      {
        id: 'throughProduct',
        label: 'Product',
        direction: 'output',
        type: 'entity.product',
        portKind: 'context',
        conceptType: 'selection',
        valueOrigin: 'through',
        defaultVisible: true,
      },
      {
        id: 'throughSelection',
        label: 'Current Choice',
        direction: 'output',
        type: 'context.selection',
        portKind: 'context',
        conceptType: 'selection',
        valueOrigin: 'through',
        defaultVisible: true,
      },
    ],
  },
  'customiser-colour-filter': {
    type: 'customiser-colour-filter',
    label: 'Colour Picker',
    description: 'Lets the visitor pick colours and narrow the matching results.',
    tier: 'utility',
    kind: 'block',
    category: 'display',
    sockets: [
      {
        id: 'selection',
        label: 'Current Choice',
        direction: 'input',
        type: 'context.selection',
        portKind: 'context',
        conceptType: 'selection',
        defaultVisible: true,
      },
      {
        id: 'reset',
        label: 'Reset',
        direction: 'input',
        type: 'event.trigger',
        portKind: 'trigger',
        defaultVisible: true,
      },
      {
        id: 'product',
        label: 'Product',
        direction: 'input',
        type: 'entity.product',
        portKind: 'context',
        conceptType: 'selection',
        defaultVisible: true,
      },
      {
        id: 'finish',
        label: 'Finish',
        direction: 'input',
        type: 'scalar.string',
        required: true,
        portKind: 'data',
        defaultVisible: true,
      },
      {
        id: 'colours',
        label: 'Chosen Colours',
        direction: 'output',
        type: 'list.string',
        portKind: 'data',
        defaultVisible: true,
      },
      {
        id: 'coloursChanged',
        label: 'Colours Picked',
        direction: 'output',
        type: 'event.trigger',
        portKind: 'trigger',
        defaultVisible: true,
      },
      {
        id: 'updatedSelection',
        label: 'Updated Choice',
        direction: 'output',
        type: 'context.selection',
        portKind: 'context',
        conceptType: 'selection',
        defaultVisible: true,
      },
      {
        id: 'throughProduct',
        label: 'Product',
        direction: 'output',
        type: 'entity.product',
        portKind: 'context',
        conceptType: 'selection',
        valueOrigin: 'through',
        defaultVisible: true,
      },
      {
        id: 'throughFinish',
        label: 'Finish',
        direction: 'output',
        type: 'scalar.string',
        portKind: 'data',
        valueOrigin: 'through',
        defaultVisible: true,
      },
      {
        id: 'throughSelection',
        label: 'Current Choice',
        direction: 'output',
        type: 'context.selection',
        portKind: 'context',
        conceptType: 'selection',
        valueOrigin: 'through',
        defaultVisible: true,
      },
    ],
  },
  'customiser-combination-results': {
    type: 'customiser-combination-results',
    label: 'Matching Results',
    description: 'Shows the matching colourways and updates the current choice.',
    tier: 'hero',
    kind: 'block',
    category: 'display',
    sockets: [
      {
        id: 'selection',
        label: 'Current Choice',
        direction: 'input',
        type: 'context.selection',
        portKind: 'context',
        conceptType: 'selection',
        defaultVisible: true,
      },
      {
        id: 'reset',
        label: 'Reset',
        direction: 'input',
        type: 'event.trigger',
        portKind: 'trigger',
        defaultVisible: true,
      },
      {
        id: 'product',
        label: 'Product',
        direction: 'input',
        type: 'entity.product',
        portKind: 'context',
        conceptType: 'selection',
        defaultVisible: true,
      },
      {
        id: 'finish',
        label: 'Finish',
        direction: 'input',
        type: 'scalar.string',
        required: true,
        portKind: 'data',
        defaultVisible: true,
      },
      {
        id: 'colours',
        label: 'Colours',
        direction: 'input',
        type: 'list.string',
        required: true,
        portKind: 'data',
        defaultVisible: true,
      },
      {
        id: 'combination',
        label: 'Chosen Colourway',
        direction: 'output',
        type: 'entity.combination',
        portKind: 'data',
        defaultVisible: true,
      },
      {
        id: 'combinationSelected',
        label: 'Colourway Picked',
        direction: 'output',
        type: 'event.trigger',
        portKind: 'trigger',
        defaultVisible: true,
      },
      {
        id: 'updatedSelection',
        label: 'Updated Choice',
        direction: 'output',
        type: 'context.selection',
        portKind: 'context',
        conceptType: 'selection',
        defaultVisible: true,
      },
      {
        id: 'throughProduct',
        label: 'Product',
        direction: 'output',
        type: 'entity.product',
        portKind: 'context',
        conceptType: 'selection',
        valueOrigin: 'through',
        defaultVisible: true,
      },
      {
        id: 'throughFinish',
        label: 'Finish',
        direction: 'output',
        type: 'scalar.string',
        portKind: 'data',
        valueOrigin: 'through',
        defaultVisible: true,
      },
      {
        id: 'throughColours',
        label: 'Colours',
        direction: 'output',
        type: 'list.string',
        portKind: 'data',
        valueOrigin: 'through',
      },
      {
        id: 'throughSelection',
        label: 'Current Choice',
        direction: 'output',
        type: 'context.selection',
        portKind: 'context',
        conceptType: 'selection',
        valueOrigin: 'through',
        defaultVisible: true,
      },
    ],
  },
  'request-form': {
    type: 'request-form',
    label: 'Request Form',
    description: 'Shows the request list and sends the enquiry.',
    tier: 'utility',
    kind: 'block',
    category: 'display',
    sockets: [
      {
        id: 'addSelection',
        label: 'Add Choice',
        direction: 'input',
        type: 'context.selection',
        portKind: 'context',
        conceptType: 'selection',
        defaultVisible: true,
      },
      {
        id: 'addItem',
        label: 'Add Request Item',
        direction: 'input',
        type: 'context.item',
        portKind: 'context',
        conceptType: 'item',
      },
      {
        id: 'clearList',
        label: 'Clear Request',
        direction: 'input',
        type: 'event.trigger',
        portKind: 'trigger',
        defaultVisible: true,
      },
      {
        id: 'requestItems',
        label: 'Request Items',
        direction: 'input',
        type: 'list.request-item',
        required: true,
        portKind: 'data',
      },
      {
        id: 'submitSuccess',
        label: 'Submitted',
        direction: 'output',
        type: 'event.trigger',
        portKind: 'trigger',
        defaultVisible: true,
      },
      {
        id: 'removedItemId',
        label: 'Removed Item ID',
        direction: 'output',
        type: 'scalar.string',
        portKind: 'data',
        defaultVisible: true,
      },
    ],
  },
  'product-deconstructor': {
    type: 'product-deconstructor',
    label: 'Product Details',
    description: 'Breaks a product into simple values like name, price, and material.',
    tier: 'utility',
    kind: 'block',
    category: 'data',
    sockets: [
      { id: 'product', label: 'Product', direction: 'input', type: 'entity.product', required: true },
      { id: 'name', label: 'Name', direction: 'output', type: 'scalar.string' },
      { id: 'price', label: 'Price', direction: 'output', type: 'scalar.number' },
      { id: 'material', label: 'Material', direction: 'output', type: 'scalar.string' },
    ],
  },
  'source-selector': {
    type: 'source-selector',
    label: 'Use Saved Value',
    description: 'Pull one saved value into the current board.',
    tier: 'utility',
    kind: 'logic',
    category: 'context',
    defaultConfig: {
      fieldId: VASE_FLOW_FIELD_IDS.chosenProduct,
    },
    sockets: [
      {
        id: 'value',
        label: 'Value',
        direction: 'output',
        resolveType: getSourceSelectorOutputType,
        portKind: 'data',
      },
    ],
  },
  'build-item': {
    type: 'build-item',
    label: 'Make Request Item',
    description: 'Turns the current choice into a request item you can add to the form.',
    tier: 'utility',
    kind: 'logic',
    category: 'context',
    sockets: [
      {
        id: 'selection',
        label: 'Current Choice',
        direction: 'input',
        type: 'context.selection',
        portKind: 'context',
        conceptType: 'selection',
        required: true,
      },
      {
        id: 'item',
        label: 'Item',
        direction: 'output',
        type: 'context.item',
        portKind: 'context',
        conceptType: 'item',
        defaultVisible: true,
      },
    ],
  },
  'selection-collector': {
    type: 'selection-collector',
    label: 'Merge Choices',
    description: 'Combines choices from different places into one shared choice or request list.',
    tier: 'utility',
    kind: 'logic',
    category: 'context',
    defaultConfig: {
      inputCount: 2,
      collectorMode: 'latest',
    },
    sockets: buildSelectionCollectorSockets(null),
  },
  'trigger-collector': {
    type: 'trigger-collector',
    label: 'When Any Happens',
    description: 'Runs the next node when any connected event happens.',
    tier: 'utility',
    kind: 'logic',
    category: 'actions',
    defaultConfig: {
      inputCount: 2,
    },
    sockets: buildTriggerCollectorSockets(null),
  },
  'global-portal': {
    type: 'global-portal',
    label: 'Send to Another Section',
    description: 'Sends choices, request items, text, or triggers to a receiver in another section.',
    tier: 'utility',
    kind: 'logic',
    category: 'context',
    defaultConfig: {
      portalName: 'Section Link',
    },
    sockets: [
      {
        id: 'selection',
        label: 'Current Choice',
        direction: 'input',
        type: 'context.selection',
        portKind: 'context',
        conceptType: 'selection',
        defaultVisible: true,
      },
      {
        id: 'item',
        label: 'Item',
        direction: 'input',
        type: 'context.item',
        portKind: 'context',
        conceptType: 'item',
        defaultVisible: true,
      },
      {
        id: 'items',
        label: 'Request Items',
        direction: 'input',
        type: 'list.request-item',
        portKind: 'data',
        defaultVisible: true,
      },
      {
        id: 'value',
        label: 'Text / ID',
        direction: 'input',
        type: 'scalar.string',
        portKind: 'data',
        defaultVisible: true,
      },
      {
        id: 'trigger',
        label: 'Run',
        direction: 'input',
        type: 'event.trigger',
        portKind: 'trigger',
        defaultVisible: true,
      },
    ],
  },
  'portal-receiver': {
    type: 'portal-receiver',
    label: 'Receive from Another Section',
    description: 'Receives choices, request items, text, or triggers from another section.',
    tier: 'utility',
    kind: 'logic',
    category: 'context',
    defaultConfig: {
      senderRef: '',
      senderFlowId: '',
      senderNodeId: '',
    },
    sockets: [
      {
        id: 'selection',
        label: 'Current Choice',
        direction: 'output',
        type: 'context.selection',
        portKind: 'context',
        conceptType: 'selection',
      },
      {
        id: 'item',
        label: 'Item',
        direction: 'output',
        type: 'context.item',
        portKind: 'context',
        conceptType: 'item',
      },
      {
        id: 'items',
        label: 'Request Items',
        direction: 'output',
        type: 'list.request-item',
        portKind: 'data',
      },
      {
        id: 'value',
        label: 'Text / ID',
        direction: 'output',
        type: 'scalar.string',
        portKind: 'data',
      },
      {
        id: 'trigger',
        label: 'Run',
        direction: 'output',
        type: 'event.trigger',
        portKind: 'trigger',
      },
    ],
  },
  'add-to-list': {
    type: 'add-to-list',
    label: 'Add to Request',
    description: 'Adds one or more request items into the request list.',
    tier: 'utility',
    kind: 'logic',
    category: 'actions',
    defaultConfig: {
      itemCount: 1,
    },
    sockets: buildAddToListSockets(null),
  },
  'remove-from-list': {
    type: 'remove-from-list',
    label: 'Remove from Request',
    description: 'Removes a matching item from the request list.',
    tier: 'utility',
    kind: 'logic',
    category: 'actions',
    sockets: [
      { id: 'list', label: 'Request Items', direction: 'input', type: 'list.request-item', required: true, portKind: 'data' },
      { id: 'item', label: 'Request Item', direction: 'input', type: 'context.item', required: true, portKind: 'context', conceptType: 'item' },
      {
        id: 'updatedList',
        label: 'Updated Request',
        direction: 'output',
        type: 'list.request-item',
        portKind: 'data',
        defaultVisible: true,
      },
    ],
  },
  'clear-list': {
    type: 'clear-list',
    label: 'Clear Request',
    description: 'Clears the request list.',
    tier: 'utility',
    kind: 'logic',
    category: 'actions',
    sockets: [
      {
        id: 'trigger',
        label: 'Run',
        direction: 'input',
        type: 'event.trigger',
        portKind: 'trigger',
        defaultVisible: true,
      },
      { id: 'list', label: 'Request Items', direction: 'input', type: 'list.request-item', required: true, portKind: 'data' },
      {
        id: 'updatedList',
        label: 'Updated Request',
        direction: 'output',
        type: 'list.request-item',
        portKind: 'data',
        defaultVisible: true,
      },
    ],
  },
  'show-message': {
    type: 'show-message',
    label: 'Show Message',
    description: 'Shows a short message when something happens.',
    tier: 'utility',
    kind: 'logic',
    category: 'actions',
    defaultConfig: {
      message: 'Item added to request.',
      tone: 'success',
    },
    sockets: [
      {
        id: 'trigger',
        label: 'Run',
        direction: 'input',
        type: 'event.trigger',
        portKind: 'trigger',
        defaultVisible: true,
      },
    ],
  },
  'set-step-tracker': {
    type: 'set-step-tracker',
    label: 'Set Step Tracker',
    description: 'Moves a Step Tracker to a chosen step when something happens.',
    tier: 'utility',
    kind: 'logic',
    category: 'actions',
    defaultConfig: {
      trackerId: '',
      stepId: 'design',
    },
    sockets: [
      {
        id: 'trigger',
        label: 'Run',
        direction: 'input',
        type: 'event.trigger',
        portKind: 'trigger',
        defaultVisible: true,
      },
    ],
  },
  'scroll-to-section': {
    type: 'scroll-to-section',
    label: 'Scroll to Section',
    description: 'Moves the page to a section when something happens.',
    tier: 'utility',
    kind: 'logic',
    category: 'actions',
    defaultConfig: {
      targetId: '',
    },
    sockets: [
      {
        id: 'trigger',
        label: 'Run',
        direction: 'input',
        type: 'event.trigger',
        portKind: 'trigger',
        defaultVisible: true,
      },
    ],
  },
  compare: {
    type: 'compare',
    label: 'Compare Values',
    description: 'Checks two values and tells you whether the rule passes.',
    tier: 'utility',
    kind: 'logic',
    category: 'data',
    defaultConfig: {
      operator: 'equals',
      valueType: 'scalar.string',
    },
    sockets: [
      {
        id: 'leftValue',
        label: 'Value A',
        direction: 'input',
        resolveType: (node) => getConfiguredValueType(node, 'scalar.string'),
        required: true,
        portKind: 'data',
      },
      {
        id: 'rightValue',
        label: 'Value B',
        direction: 'input',
        resolveType: (node) => getConfiguredValueType(node, 'scalar.string'),
        portKind: 'data',
      },
      {
        id: 'result',
        label: 'Match',
        direction: 'output',
        type: 'scalar.boolean',
        portKind: 'data',
      },
    ],
  },
  condition: {
    type: 'condition',
    label: 'If / Else',
    description: 'Uses a yes or no result to choose the next action or value.',
    tier: 'utility',
    kind: 'logic',
    category: 'data',
    defaultConfig: {
      valueType: 'scalar.string',
    },
    sockets: [
      { id: 'check', label: 'Value A', direction: 'input', type: 'scalar.string', portKind: 'data' },
      { id: 'against', label: 'Value B', direction: 'input', type: 'scalar.string', portKind: 'data' },
      { id: 'ifTrue', label: 'If Yes', direction: 'output', type: 'event.trigger', portKind: 'trigger' },
      { id: 'ifFalse', label: 'If No', direction: 'output', type: 'event.trigger', portKind: 'trigger' },
      { id: 'condition', label: 'Rule Result', direction: 'input', type: 'scalar.boolean', required: true, portKind: 'data' },
      {
        id: 'trueValue',
        label: 'Yes Value',
        direction: 'input',
        resolveType: (node) => getConfiguredValueType(node, 'scalar.string'),
        portKind: 'data',
      },
      {
        id: 'falseValue',
        label: 'No Value',
        direction: 'input',
        resolveType: (node) => getConfiguredValueType(node, 'scalar.string'),
        portKind: 'data',
      },
      {
        id: 'result',
        label: 'Chosen Value',
        direction: 'output',
        resolveType: (node) => getConfiguredValueType(node, 'scalar.string'),
        portKind: 'data',
      },
    ],
  },
};

export const LOGIC_ONLY_NODE_TYPES = new Set(
  Object.values(FLOW_NODE_DEFINITIONS)
    .filter((definition) => definition.kind === 'logic')
    .map((definition) => definition.type)
);

export const FLOW_NODE_LIBRARY_ITEMS: FlowNodeLibraryItem[] = [
  {
    id: 'logic-node-source-selector',
    kind: 'logic-node',
    nodeType: 'source-selector',
    label: 'Use Saved Value',
    description: 'Bring one saved value into this board.',
    category: 'context',
  },
  {
    id: 'logic-node-build-item',
    kind: 'logic-node',
    nodeType: 'build-item',
    label: 'Make Request Item',
    description: 'Turn the current choice into a request item.',
    category: 'context',
  },
  {
    id: 'logic-node-selection-collector',
    kind: 'logic-node',
    nodeType: 'selection-collector',
    label: 'Merge Choices',
    description: 'Combine choices from different places into one shared choice or request list.',
    category: 'context',
  },
  {
    id: 'logic-node-global-portal',
    kind: 'logic-node',
    nodeType: 'global-portal',
    label: 'Send to Another Section',
    description: 'Send choices, request items, or triggers to another section.',
    category: 'context',
  },
  {
    id: 'logic-node-portal-receiver',
    kind: 'logic-node',
    nodeType: 'portal-receiver',
    label: 'Receive from Another Section',
    description: 'Receive choices, request items, or triggers from another section.',
    category: 'context',
  },
  {
    id: 'logic-node-add-to-list',
    kind: 'logic-node',
    nodeType: 'add-to-list',
    label: 'Add to Request',
    description: 'Add one or more request items into the request list.',
    category: 'actions',
  },
  {
    id: 'logic-node-remove-from-list',
    kind: 'logic-node',
    nodeType: 'remove-from-list',
    label: 'Remove from Request',
    description: 'Remove a matching request item from the request list.',
    category: 'actions',
  },
  {
    id: 'logic-node-clear-list',
    kind: 'logic-node',
    nodeType: 'clear-list',
    label: 'Clear Request',
    description: 'Clear the request list.',
    category: 'actions',
  },
  {
    id: 'logic-node-trigger-collector',
    kind: 'logic-node',
    nodeType: 'trigger-collector',
    label: 'When Any Happens',
    description: 'Run one action from several events.',
    category: 'actions',
  },
  {
    id: 'logic-node-show-message',
    kind: 'logic-node',
    nodeType: 'show-message',
    label: 'Show Message',
    description: 'Show a short message when something happens.',
    category: 'actions',
  },
  {
    id: 'logic-node-set-step-tracker',
    kind: 'logic-node',
    nodeType: 'set-step-tracker',
    label: 'Set Step Tracker',
    description: 'Move the page Step Tracker to a chosen step.',
    category: 'actions',
  },
  {
    id: 'logic-node-scroll-to-section',
    kind: 'logic-node',
    nodeType: 'scroll-to-section',
    label: 'Scroll to Section',
    description: 'Move the page to a section when something happens.',
    category: 'actions',
  },
  {
    id: 'logic-node-compare',
    kind: 'logic-node',
    nodeType: 'compare',
    label: 'Compare Values',
    description: 'Check whether two values match a rule.',
    category: 'data',
  },
  {
    id: 'logic-node-condition',
    kind: 'logic-node',
    nodeType: 'condition',
    label: 'If / Else',
    description: 'Choose what happens next based on a yes or no result.',
    category: 'data',
  },
];

export function getFlowNodeDefinition(type: string | null | undefined): FlowNodeDefinition | null {
  if (!type) return null;
  return FLOW_NODE_DEFINITIONS[type] ?? null;
}

export function resolveFlowNodeSocketType(
  node: FlowNode,
  socketId: string,
  flow: FlowDefinition | null
): FlowValueType | null {
  const socket = resolveFlowNodeSockets(node, flow).find((entry) => entry.id === socketId) ?? null;
  if (!socket) return null;
  if (socket.resolveType) return socket.resolveType(node, flow);
  return socket.type ?? null;
}

export function resolveFlowNodeSockets(
  node: FlowNode | null | undefined,
  flow: FlowDefinition | null
) {
  if (!node) return [] as FlowNodeSocketDefinition[];
  const definition = getFlowNodeDefinition(node.blockType);
  if (!definition) return [] as FlowNodeSocketDefinition[];
  if (node.blockType === 'selection-collector') {
    return applyConfiguredSocketOrder(node, buildSelectionCollectorSockets(node));
  }
  if (node.blockType === 'trigger-collector') {
    return applyConfiguredSocketOrder(node, buildTriggerCollectorSockets(node));
  }
  if (node.blockType === 'add-to-list') {
    return applyConfiguredSocketOrder(node, buildAddToListSockets(node));
  }
  return applyConfiguredSocketOrder(node, definition.sockets);
}

export function isLogicOnlyFlowNodeType(type: string | null | undefined) {
  return Boolean(type && LOGIC_ONLY_NODE_TYPES.has(type));
}

export function getCompareOperatorsForValueType(valueType: FlowValueType): CompareOperator[] {
  if (valueType === 'context.selection' || valueType === 'context.item') {
    return ['equals', 'notEquals', 'isEmpty', 'isNotEmpty'];
  }
  if (valueType === 'scalar.number') {
    return ['equals', 'notEquals', 'greaterThan', 'lessThan', 'isEmpty', 'isNotEmpty'];
  }
  if (valueType === 'list.string' || valueType === 'list.request-item') {
    return ['contains', 'isEmpty', 'isNotEmpty'];
  }
  return ['equals', 'notEquals', 'contains', 'isEmpty', 'isNotEmpty'];
}

export function getSelectableSourceFieldIds() {
  return [
    VASE_FLOW_FIELD_IDS.chosenProduct,
    VASE_FLOW_FIELD_IDS.chosenFinish,
    VASE_FLOW_FIELD_IDS.chosenColours,
    VASE_FLOW_FIELD_IDS.chosenCombination,
    REQUEST_FLOW_FIELD_IDS.requestItems,
    VASE_FLOW_FIELD_IDS.productName,
    VASE_FLOW_FIELD_IDS.productPrice,
    VASE_FLOW_FIELD_IDS.productMaterial,
    REQUEST_FLOW_FIELD_IDS.currentRequestItem,
  ];
}
